import QuoteApprovalCaution from './QuoteApprovalCaution';
import { toast } from 'react-toastify';
import { listingNavigationButtonSx } from './navigationStyles';
import { requestAdminNavigation } from 'src/navigation/adminNavigationGuard';
import { QuoteNavigationActionsContext } from 'src/content/applications/quote/QuoteEditorHeaderActions';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import BlockOutlined from '@mui/icons-material/BlockOutlined';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import { ReactNode, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Link,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
  Stack,
  Portal,
  Typography
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { useAdminWorkspace } from 'src/workspaces/AdminWorkspaceProvider';

type Publication = {
  eventId: number;
  companyId: number;
  published: boolean;
  activeShiftCount?: number;
  quoteStatus?: string;
  quoteApproved?: boolean;
  everPublished: boolean;
  unpublishedAt: string | null;
  state: string;
  publishedAt: string | null;
  publishedBy: number | null;
  deferredAt: string | null;
  readyToPublish: boolean;
  blockingReasons: string[];
  publicationWarnings?: string[];
  advisoryReasons: string[];
  workerRatesConfigured?: boolean;
};
export const WORKER_RATES_ANCHOR_ID = 'worker-rates-bid-type';
const eventName = 'avwf:quote-approved';
export function usePublication(eventId: number | null) {
  const workspace = useAdminWorkspace();
  const company =
    workspace.kind === 'company' && workspace.status === 'ready'
      ? workspace.workspace
      : null;
  const companyId = company?.company.id;
  const allowed = Boolean(
    company &&
      (company.accessLevels ?? [company.accessLevel]).some(
        (level) => level === 'COMPANY_ADMIN' || level === 'OPERATIONS_MANAGER'
      )
  );
  const path = `/companies/${companyId}/events/${eventId}/publication`;
  const queryClient = useQueryClient();
  const query = useQuery<Publication, Error>({
    queryKey: ['job-publication', companyId, eventId],
    enabled: Boolean(companyId && eventId),
    queryFn: async () => (await axios.get(path)).data.data
  });
  const keys = useRef<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const act = async (action: 'publish' | 'defer' | 'unpublish', allowPublicationWarnings = false) => {
    if (!allowed || !eventId || !companyId || busy) return false;
    const attempt = `${companyId}:${eventId}:${action}:${allowPublicationWarnings}`;
    keys.current[attempt] ??= `publication:${crypto.randomUUID()}`;
    setBusy(true);
    setError('');
    try {
      const result = await axios.post(
        path,
        { action, ...(allowPublicationWarnings ? { allowPublicationWarnings: true } : {}) },
        { headers: { 'Idempotency-Key': keys.current[attempt] } }
      );
      queryClient.setQueryData(
        ['job-publication', companyId, eventId],
        result.data.data
      );
      await queryClient.invalidateQueries({ queryKey: ['job-publication'] });
      await queryClient.invalidateQueries({
        queryKey: ['contract-lifecycle', companyId, eventId]
      });
      delete keys.current[attempt];
      return true;
    } catch (failure) {
      setError(
        (failure as { response?: { data?: { message?: string } } }).response
          ?.data?.message ||
          'Unable to update job listings. Retry the same action.'
      );
      // Refresh warnings after a concurrent edit; the user still confirms any retry.
      void query.refetch();
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { ...query, allowed, visible: Boolean(companyId), busy, error, act };
}

export function JobPublicationStatus({
  eventId,
  onViewBids,
  actionButtonSx,
  embedded = false,
  footer = false,
  feedbackContainer,
  actionContainer,
  compactCaution = false,
  hideActions = false,
  hideSplitStatus = false,
  hidePublishedUpdate = false,
  statusDropdown = false,
  publishContainer
}: {
  /** Footer split layout: Unpublish stays in actionContainer, Publish/Update renders here. */
  publishContainer?: () => HTMLElement | null;
  eventId: number;
  compactCaution?: boolean;
  hideActions?: boolean;
  hideSplitStatus?: boolean;
  hidePublishedUpdate?: boolean;
  statusDropdown?: boolean;
  embedded?: boolean;
  footer?: boolean;
  feedbackContainer?: HTMLElement | null;
  actionContainer?: () => HTMLElement | null;
  onViewBids?: () => void;
  actionButtonSx?: SxProps<Theme>;
}) {
  const publication = usePublication(eventId);
  const navigationContainer = useContext(QuoteNavigationActionsContext);
  const [confirm, setConfirm] = useState(false);
  const [republish, setRepublish] = useState(false);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<HTMLElement | null>(null);
  const renderFeedback = (content: ReactNode) => {
    if (footer && !feedbackContainer) return null;
    return <Portal container={feedbackContainer} disablePortal={!footer}>{content}</Portal>;
  };
  if (!publication.visible) return onViewBids ? (
    <Box sx={{ m: 2, display: 'flex', justifyContent: 'flex-end' }}>
      <Button variant="contained" onClick={onViewBids}>View All Bids</Button>
    </Box>
  ) : null;
  if (publication.isLoading)
    return (
      <Box sx={{ p: 2 }}>
        <CircularProgress size={20} aria-label="Loading publication status" />
      </Box>
    );
  if (publication.isError)
    return renderFeedback(
      <Alert
        severity="error"
        action={<Button onClick={() => publication.refetch()}>Retry</Button>}
      >
        Unable to load job publication status.
      </Alert>
    );
  const state = publication.data;
  if (!state) return null;
  const canceled = state.state === 'CANCELED';
  const empty = state.activeShiftCount === 0;
  const approved = state.quoteApproved ?? !['AWAITING_APPROVAL'].includes(state.state);
  const quoteStatusLabel = state.quoteStatus
    ? state.quoteStatus.charAt(0).toUpperCase() + state.quoteStatus.slice(1)
    : 'Draft';
  const wasPublished = state.everPublished || Boolean(state.publishedAt);
  const action = state.published && !republish ? 'unpublish' : 'publish';
  const publicationBlocked =
    !state.published &&
    (state.readyToPublish === false || (state.blockingReasons?.length ?? 0) > 0);
  const unconfiguredPublish = !state.published && state.workerRatesConfigured === false;
  const split = Boolean(publishContainer);
  const canUnpublishFromStatus = statusDropdown && state.published && !canceled && publication.allowed && !hideActions;
  const statusMenuId = `job-publication-menu-${eventId}`;
  const publishedStatusSx = { width: 150, height: 34, borderRadius: '8px', border: '1px solid #2e7d32', backgroundColor: '#edf7ed', color: '#1b5e20', fontWeight: 800 };
  const warnings = state.publicationWarnings ?? [];
  const pageWarnings = [...warnings, ...(state.advisoryReasons ?? [])];
  const openPublish = () => {
    if (publicationBlocked) { toast.error(state.blockingReasons?.join(' ') || 'Publication could not be validated. Retry loading its status.'); return; }
    setRepublish(state.published);
    setConfirm(true);
  };
  return (
    <>
      {!approved && !canceled && !compactCaution && renderFeedback(
        <Alert severity="warning" sx={{ m: embedded ? 0 : 2, mb: 1 }}>
          <strong>Quote Not Approved</strong> — Publishing opens worker bidding; it does not approve the quote or change deposits.{' '}
          <Link href={`/dashboards/jobTracker/positions/${eventId}?tab=quote`}
            onClick={(event) => {
              const path = `/dashboards/jobTracker/positions/${eventId}?tab=quote`;
              if (!requestAdminNavigation(() => window.location.assign(path))) event.preventDefault();
            }}>Review quote</Link>
        </Alert>
      )}
      <Stack
        direction={embedded ? 'column' : { xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{
          m: embedded ? 0 : 2,
          p: embedded ? 0 : onViewBids ? 2 : 2.5,
          bgcolor: 'background.paper',
          border: embedded ? 'none' : '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          alignItems: embedded ? 'flex-end' : { sm: 'center' },
          ...(embedded ? { textAlign: 'right' } : {}),
          ...(onViewBids ? {
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 260px minmax(0, 1fr)' },
            gap: 2,
            '& > :not(style) ~ :not(style)': { margin: 0 }
          } : {}),
          ...(footer ? {
            m: 0, px: { xs: 2, sm: 3, md: 6 }, py: 1.5, pb: 'max(12px, env(safe-area-inset-bottom))', display: compactCaution ? 'flex' : 'grid', gridTemplateColumns: 'auto minmax(0, 1fr) auto', flexDirection: 'row',
            flexWrap: compactCaution ? 'wrap' : 'nowrap', alignItems: 'center', gap: 2,
            border: 0, borderRadius: 0, bgcolor: 'transparent',
            whiteSpace: 'nowrap', minWidth: 0,
            '& button': { whiteSpace: 'nowrap', flexShrink: 0, height: 36, minHeight: 36, py: 0 },
            '& > :not(style) ~ :not(style)': { margin: 0 }
          } : {})
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: footer ? 'contents' : onViewBids ? 'flex' : 'block',
            ...(onViewBids ? { gridColumn: { lg: 1 }, gridRow: { lg: 1 }, textAlign: 'left' } : {}),
            alignItems: 'center',
            gap: 1.25,
            flexWrap: footer || (onViewBids && state.published) ? 'nowrap' : 'wrap'
          }}
        >
          <Typography variant="h5" sx={onViewBids ? { textAlign: 'left', whiteSpace: 'nowrap' } : undefined}>
            Job Listings{' '}
            {canUnpublishFromStatus ? <Button
              size="small"
              variant="outlined"
              aria-label="Published - Job Listing Actions"
              aria-haspopup="menu"
              aria-controls={statusMenuAnchor ? statusMenuId : undefined}
              aria-expanded={Boolean(statusMenuAnchor)}
              disabled={publication.busy}
              startIcon={<CheckCircleRounded fontSize="small" />}
              endIcon={<KeyboardArrowDown fontSize="small" />}
              onClick={event => setStatusMenuAnchor(event.currentTarget)}
              sx={{ ...publishedStatusSx, ml: 1, px: 1, '&:hover': { backgroundColor: publishedStatusSx.backgroundColor, border: publishedStatusSx.border }, '& .MuiButton-startIcon, & .MuiButton-endIcon': { color: '#2e7d32' } }}
            >Published</Button> : <Chip
              size="small"
              icon={state.published ? <CheckCircleRounded /> : undefined}
              label={
                empty && !state.published
                  ? 'No active shifts'
                  : state.published
                  ? 'Published'
                  : state.state === 'CANCELED'
                  ? 'Canceled'
                  : wasPublished
                  ? 'Unpublished'
                  : 'Not published'
              }
              sx={{ ml: 1, ...(state.published ? { ...publishedStatusSx, '.MuiChip-icon': { color: '#2e7d32', ml: 1 } } : {}) }}
            />}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            title={onViewBids && state.publishedAt ? `Published ${new Date(state.publishedAt).toLocaleString()}` : undefined}
            sx={{ display: footer ? 'none' : undefined, mt: onViewBids ? 0 : 1, ...(onViewBids ? { textAlign: 'left', whiteSpace: state.published ? 'nowrap' : 'normal' } : {}) }}
          >
            {empty && !state.published
              ? 'No active shifts to publish. Existing bids and historical records remain available for review.'
              : state.published
              ? `Live for eligible workers${
                  !onViewBids && state.publishedAt
                    ? ` · Published ${new Date(
                        state.publishedAt
                      ).toLocaleString()}`
                    : ''
                }`
              : state.state === 'CANCELED'
              ? 'This event is canceled.'
              : wasPublished
              ? 'Hidden from workers. Existing bids remain available for review; publish again to assign bids.'
              : publicationBlocked
              ? state.blockingReasons?.join(' ') || 'Complete the required publication steps before publishing.'
              : !approved
              ? 'Publish explicitly to open these listings to eligible workers. Client approval and deposits remain unchanged.'
              : 'Workers cannot see these listings or place any bids until jobs are published.'}
          </Typography>
          {pageWarnings.length > 0 && renderFeedback(
            <Alert severity="warning" sx={{ mt: 1 }}>
              {pageWarnings.join(' ')}
            </Alert>
          )}
        </Box>
        {compactCaution && !approved && !canceled && <QuoteApprovalCaution eventId={eventId} />}
        {!hideActions && <Portal container={actionContainer} disablePortal={!actionContainer}>
        <Box sx={{ display: onViewBids ? 'contents' : 'flex', gap: 2, flexWrap: 'wrap', ...(onViewBids ? { gridColumn: 1, gridRow: 1, justifySelf: 'start' } : {}) }}>
        {onViewBids && (
          <Button
            variant="contained"
            sx={{ ...listingNavigationButtonSx(state.published), gridColumn: footer ? 3 : { lg: 2 }, gridRow: footer ? 1 : { lg: 1 }, justifySelf: footer ? 'end' : 'center' }}
            onClick={onViewBids}
          >
            View All Bids
          </Button>
        )}
        {!canUnpublishFromStatus && !canceled && (!empty || state.published) && publication.allowed && (!split || state.published) && (
          <Portal container={navigationContainer} disablePortal={!footer || !navigationContainer || Boolean(actionContainer)}>
          <Button variant={state.published || unconfiguredPublish ? 'outlined' : 'contained'} sx={state.published ? { gridColumn: { lg: 3 }, gridRow: { lg: 1 }, justifySelf: { lg: 'end' }, backgroundColor: 'transparent', color: '#b42318', borderColor: '#b42318', boxShadow: 'none', '&:hover': { backgroundColor: 'transparent', color: '#912018', borderColor: '#912018', boxShadow: 'none' } } : unconfiguredPublish ? {
            ...(typeof actionButtonSx === 'object' && !Array.isArray(actionButtonSx) ? actionButtonSx : {}),
            borderColor: '#111', color: '#111', backgroundColor: 'transparent', boxShadow: 'none',
            '&:hover': { borderColor: '#111', backgroundColor: '#f5f5f5', boxShadow: 'none' }
          } : actionButtonSx}
            onClick={() => {
            if (!state.published && publicationBlocked) { toast.error(state.blockingReasons?.join(' ') || 'Publication could not be validated. Retry loading its status.'); return; }
            setRepublish(false);
            setConfirm(true);
          }}>
            {state.published
              ? 'Unpublish Job Listings'
              : 'Publish Job Listings'}
          </Button>
          </Portal>
        )}
        </Box>
        </Portal>}
        {!hideActions && !(hidePublishedUpdate && state.published) && split && !canceled && (!empty || state.published) && publication.allowed && (
          <Portal container={publishContainer}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {!hideSplitStatus && <Typography variant="body2" data-testid="footer-publish-status" sx={{ fontWeight: 700, color: state.published ? '#1b5e20' : 'text.secondary' }}>
                {state.published ? 'Published' : 'Not published'}
              </Typography>}
              <Button size="small" variant="contained" disabled={publication.busy} onClick={openPublish}>
                {state.published ? 'Update Job Listing' : 'Publish Job Listing'}
              </Button>
            </Box>
          </Portal>
        )}
      </Stack>
      <Menu
        id={statusMenuId}
        anchorEl={statusMenuAnchor}
        open={canUnpublishFromStatus && Boolean(statusMenuAnchor)}
        onClose={() => setStatusMenuAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        PaperProps={{ sx: {
          width: statusMenuAnchor?.getBoundingClientRect().width || publishedStatusSx.width,
          maxWidth: 'calc(100vw - 32px)',
          boxSizing: 'border-box',
          p: 0,
          border: publishedStatusSx.border,
          borderRadius: publishedStatusSx.borderRadius,
          bgcolor: publishedStatusSx.backgroundColor,
          color: publishedStatusSx.color
        } }}
        MenuListProps={{ dense: true, disablePadding: true, style: { padding: 0 } }}
      >
        <MenuItem sx={{ minHeight: publishedStatusSx.height, justifyContent: 'center', gap: 1, px: 1, py: 0.5, '&&&': { color: 'error.main' } }}
          onClick={() => { setStatusMenuAnchor(null); setRepublish(false); setConfirm(true); }}>
          <BlockOutlined fontSize="small" sx={{ color: 'inherit' }} aria-hidden="true" />
          Unpublish
        </MenuItem>
      </Menu>
      <Dialog
        open={confirm}
        onClose={() => !publication.busy && setConfirm(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {action === 'unpublish'
            ? 'Unpublish Job Listings?'
            : !approved
            ? 'Publish Before Client Approval?'
            : 'Publish Job Listings?'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {action === 'unpublish'
              ? 'These listings will be hidden from workers. Existing bids and assignments are preserved. Bids can be reviewed but cannot be assigned until you publish again.'
              : !approved
              ? `This opens these job listings to eligible workers. The client quote stays ${quoteStatusLabel}. Client approval and deposit status do not change.`
              : wasPublished
              ? 'These listings will become visible to eligible workers again. Existing bids can be assigned after publication. Quote, invoice and worker-pay amounts stay unchanged.'
              : 'These listings will become visible to eligible workers. Workers can then place bids, and eligible bids can be assigned. Quote, invoice and worker-pay amounts stay unchanged.'}
          </Typography>
          {action === 'publish' && warnings.length > 0 && <Alert severity="warning" sx={{ mt: 2 }}>
            {warnings.join(' ')} You can continue anyway. Incomplete Required Rates cannot receive fixed-rate bids until corrected. Existing bids and accepted worker pay stay unchanged.
          </Alert>}
          {publication.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {publication.error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={publication.busy} onClick={() => setConfirm(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={publication.busy}
            onClick={async () => {
              if (await publication.act(action, action === 'publish' && warnings.length > 0)) setConfirm(false);
            }}
          >
            {action === 'unpublish' ? 'Unpublish Now' : warnings.length ? 'Publish Anyway' : state.published ? 'Update Now' : 'Publish Now'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export function JobPublicationApprovalPrompt() {
  const queryClient = useQueryClient();
  useEffect(() => {
    const listener = (event: Event) => {
      const id = Number((event as CustomEvent).detail?.eventId);
      if (Number.isSafeInteger(id) && id > 0) {
        void queryClient.invalidateQueries({ queryKey: ['job-publication'] });
        // Retained Event tabs use both route-string and numeric IDs. Refresh
        // only this approved event, leaving other editable drafts untouched.
        void queryClient.invalidateQueries({
          predicate: ({ queryKey }) =>
            (queryKey[0] === 'qoute' || queryKey[0] === 'jobs') &&
            Number(queryKey[1]) === id
        });
      }
    };
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }, [queryClient]);
  return null;
}
